/**
 * Page d'activation du compte revendeur via magic link — Phase 65.
 * URL : /boutique/activer?token=XXX
 *
 * Flow :
 * 1. Lit le token dans l'URL
 * 2. Appelle GET /api/shop/auth/reseller-activation/{token} pour récupérer
 *    les infos pré-remplies (email, nom de société, % remise)
 * 3. Affiche un form de création de mot de passe (email/nom non éditables)
 * 4. Appelle POST /api/shop/auth/reseller-activation/{token} avec le mdp
 * 5. Le backend crée/lie le compte shop, set les cookies de session, retourne le customer
 * 6. Redirige vers /boutique avec un message "Bienvenue, votre tarif revendeur est actif"
 *
 * Charte alignée sur ShopAuth.jsx (fond noir + or basilic).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Mail,
  Lock,
  User,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Tag,
} from "lucide-react";
import { shopApi, formatShopAuthError, useShopAuth } from "../lib/shopAuth";

const COLORS = {
  bg: "#0A0A0A",
  surface: "#141414",
  primary: "#F2ECE4",
  secondary: "#A6A29C",
  muted: "#737373",
  gold: "#C1A063",
  basil: "#88A066",
};

function fmtDiscount(pct) {
  if (!pct) return "à négocier";
  return `-${Number(pct)} %`;
}

export default function ResellerActivation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useShopAuth();
  const token = params.get("token") || "";

  const [loadingPreview, setLoadingPreview] = useState(true);
  const [preview, setPreview] = useState(null);
  const [errorPreview, setErrorPreview] = useState(null);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      // Reporte le setState au prochain tick pour éviter set-state-in-effect linting
      const id = setTimeout(() => {
        setErrorPreview("Lien d'activation manquant ou invalide.");
        setLoadingPreview(false);
      }, 0);
      return () => clearTimeout(id);
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await shopApi.get(`/auth/reseller-activation/${encodeURIComponent(token)}`);
        if (alive) setPreview(data);
      } catch (e) {
        if (alive) {
          const msg = e?.response?.data?.detail || formatShopAuthError(e);
          setErrorPreview(msg);
        }
      } finally {
        if (alive) setLoadingPreview(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!password || password.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!acceptTerms) {
      setError("Veuillez accepter les conditions revendeur.");
      return;
    }
    setSubmitting(true);
    try {
      await shopApi.post(`/auth/reseller-activation/${encodeURIComponent(token)}`, {
        password,
        accept_terms: acceptTerms,
      });
      // Rafraîchit le contexte shop pour récupérer customer + reseller
      await refresh();
      navigate("/boutique?activated=1");
    } catch (e2) {
      setError(e2?.response?.data?.detail || formatShopAuthError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: COLORS.bg, color: COLORS.primary }}>
      <header className="border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/boutique" className="flex items-center gap-2">
            <img src="/logo-maisonthai-pdf.png" alt="Maison Thaï" className="w-9 h-9 protected-asset" draggable={false} onContextMenu={(e) => e.preventDefault()} onDragStart={(e) => e.preventDefault()} />
            <span className="font-heading text-xl tracking-wide" style={{ color: COLORS.primary }}>
              Maison Thaï <span style={{ color: COLORS.gold }}>Conthey</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md" data-testid="reseller-activation-card">
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.gold }} />
            </div>
          ) : errorPreview ? (
            <div
              className="border rounded p-6 text-center"
              style={{ borderColor: "rgba(229,77,46,0.4)", background: "rgba(229,77,46,0.06)" }}
              data-testid="activation-error"
            >
              <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: "#E54D2E" }} />
              <h2 className="font-heading text-xl mb-2" style={{ color: COLORS.primary }}>
                Lien invalide
              </h2>
              <p className="text-sm" style={{ color: COLORS.secondary }}>
                {errorPreview}
              </p>
              <p className="text-xs mt-4" style={{ color: COLORS.muted }}>
                Contactez Maison Thaï pour recevoir un nouveau lien :{" "}
                <a href="mailto:info@maison-thai.ch" className="underline" style={{ color: COLORS.gold }}>
                  info@maison-thai.ch
                </a>
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <Sparkles className="w-7 h-7 mx-auto mb-3" style={{ color: COLORS.gold }} />
                <h1 className="font-heading text-3xl tracking-wide mb-2" style={{ color: COLORS.primary }}>
                  Bienvenue {preview.contact_name?.split(" ")[0] || ""}
                </h1>
                <p className="text-sm" style={{ color: COLORS.secondary }}>
                  Activez votre compte revendeur en définissant un mot de passe.
                </p>
              </div>

              {/* Récap conditions */}
              <div
                className="rounded p-4 mb-6 border"
                style={{ borderColor: "rgba(193,160,99,0.3)", background: "rgba(193,160,99,0.06)" }}
                data-testid="activation-discount-badge"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.gold }}>
                  <Tag className="w-3 h-3" />
                  Vos conditions revendeur
                </div>
                <div className="mt-2 text-xl font-heading" style={{ color: COLORS.primary }}>
                  {fmtDiscount(preview.discount_percent)} <span className="text-sm" style={{ color: COLORS.secondary }}>sur le catalogue</span>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="activation-form">
                {/* Email pré-rempli, non éditable */}
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>
                    Votre email
                  </span>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.muted }} />
                    <input
                      type="email"
                      value={preview.email}
                      disabled
                      className="w-full pl-10 pr-3 py-3 text-sm bg-transparent border focus:outline-none cursor-not-allowed"
                      style={{ borderColor: "rgba(242,236,228,0.12)", color: COLORS.secondary }}
                    />
                  </div>
                </label>

                {/* Société pré-remplie */}
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>
                    Votre commerce
                  </span>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.muted }} />
                    <input
                      type="text"
                      value={preview.business_name}
                      disabled
                      className="w-full pl-10 pr-3 py-3 text-sm bg-transparent border focus:outline-none cursor-not-allowed"
                      style={{ borderColor: "rgba(242,236,228,0.12)", color: COLORS.secondary }}
                    />
                  </div>
                </label>

                {/* Mot de passe */}
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>
                    Mot de passe (8+ caractères)
                  </span>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.muted }} />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      className="w-full pl-10 pr-3 py-3 text-sm bg-transparent border focus:outline-none focus:border-[#C1A063] transition-colors"
                      style={{ borderColor: "rgba(242,236,228,0.18)", color: COLORS.primary }}
                      data-testid="activation-password"
                    />
                  </div>
                </label>

                {/* Confirmation */}
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.secondary }}>
                    Confirmer le mot de passe
                  </span>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.muted }} />
                    <input
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      className="w-full pl-10 pr-3 py-3 text-sm bg-transparent border focus:outline-none focus:border-[#C1A063] transition-colors"
                      style={{ borderColor: "rgba(242,236,228,0.18)", color: COLORS.primary }}
                      data-testid="activation-confirm"
                    />
                  </div>
                </label>

                {/* Acceptation conditions */}
                <label className="flex items-start gap-3 text-xs cursor-pointer" style={{ color: COLORS.secondary }}>
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-0.5"
                    data-testid="activation-accept"
                  />
                  <span>
                    J&apos;accepte les conditions revendeur Maison Thaï : paiement à 30 jours, retours selon CGV,
                    respect de la chaîne du froid (≤ 5°C) jusqu&apos;à la vente finale.
                  </span>
                </label>

                {error && (
                  <div
                    className="border rounded px-3 py-2 text-xs flex items-start gap-2"
                    style={{ borderColor: "rgba(229,77,46,0.4)", background: "rgba(229,77,46,0.06)", color: "#E54D2E" }}
                    data-testid="activation-error-msg"
                  >
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 mt-2 text-xs uppercase tracking-[0.25em] transition-all disabled:opacity-50"
                  style={{ background: COLORS.gold, color: COLORS.bg }}
                  data-testid="activation-submit"
                >
                  {submitting ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Activation…
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4" /> Activer mon compte
                    </span>
                  )}
                </button>

                <p className="text-[10px] text-center mt-3" style={{ color: COLORS.muted }}>
                  Une fois activé, vous serez automatiquement connecté.
                </p>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
